<template>
  <div>
    <!-- ========== dot 模式: 角落小圆点指示灯 ========== -->
    <div
      v-if="mode === 'dot'"
      class="ws-dot-wrap"
      :style="dotWrapStyle"
      @mouseenter="dotHover = true"
      @mouseleave="dotHover = false"
    >
      <span
        class="ws-dot"
        :class="'ws-dot--' + currentStatus"
        :title="statusText"
      ></span>
      <transition name="ws-popup">
        <div v-if="dotHover" class="ws-dot-popup" :class="'ws-popup--' + currentStatus">
          <span class="ws-popup-text">{{ statusText }}</span>
          <span
            v-if="showRetry"
            class="ws-popup-retry"
            @click.stop="handleRetry"
          >点击重连</span>
        </div>
      </transition>
    </div>

    <!-- ========== bar 模式: 顶部状态条 ========== -->
    <transition name="ws-slide">
      <div
        v-if="mode === 'bar' && barVisible"
        class="ws-bar"
        :class="'ws-bar--' + currentStatus"
      >
        <span class="ws-bar-dot"></span>
        <span class="ws-bar-text">{{ statusText }}</span>
        <span
          v-if="showRetry"
          class="ws-bar-retry"
          @click.stop="handleRetry"
        >手动重连</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { RWS_STATUS } from '@/utils/ReconnectableWebSocket'

export default {
  name: 'WsStatusIndicator',
  props: {
    ws: {
      type: Object,
      default: null
    },
    // 显示模式: 'dot'(角落圆点,默认) | 'bar'(顶部状态条)
    mode: {
      type: String,
      default: 'dot'
    },
    // 自动重连超过多少次后显示手动重连按钮
    retryThreshold: {
      type: Number,
      default: 3
    },
    // dot 模式下距底部的偏移量(px)
    bottomOffset: {
      type: Number,
      default: 20
    }
  },
  data () {
    return {
      currentStatus: 'disconnected',
      attempt: 0,
      maxAttempts: 10,
      hideTimer: null,
      dotHover: false
    }
  },
  computed: {
    dotWrapStyle () {
      return {
        bottom: this.bottomOffset + 'px'
      }
    },
    statusText () {
      switch (this.currentStatus) {
        case 'connected':
          return '连接正常'
        case 'connecting':
          return '连接中...'
        case 'reconnecting':
          return '重连中 (' + this.attempt + '/' + this.maxAttempts + ')'
        case 'disconnected':
          return this.attempt >= this.maxAttempts ? '连接失败' : '已断开'
        default:
          return ''
      }
    },
    showRetry () {
      return (
        (this.currentStatus === 'reconnecting' && this.attempt >= this.retryThreshold) ||
        (this.currentStatus === 'disconnected' && this.attempt > 0)
      )
    },
    // bar 模式: 连接正常时 2s 后隐藏
    barVisible () {
      return this.currentStatus !== 'connected' || this.hideTimer !== null
    }
  },
  watch: {
    ws: {
      immediate: true,
      handler (newWs) {
        this._bindWs(newWs)
      }
    }
  },
  beforeDestroy () {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
    }
  },
  methods: {
    _bindWs (ws) {
      if (!ws) return
      if (ws.status) {
        this.currentStatus = ws.status
      }
      if (ws._options) {
        this.maxAttempts = ws._options.maxReconnectAttempts || 10
      }
      ws.onStatusChange = (status, oldStatus, attempt) => {
        this.currentStatus = status
        this.attempt = attempt
        // bar 模式: 连接成功后短暂显示再隐藏
        if (this.mode === 'bar') {
          if (status === 'connected') {
            if (this.hideTimer) clearTimeout(this.hideTimer)
            this.hideTimer = setTimeout(() => {
              this.hideTimer = null
            }, 2000)
          } else {
            if (this.hideTimer) {
              clearTimeout(this.hideTimer)
              this.hideTimer = null
            }
          }
        }
      }
    },
    handleRetry () {
      if (this.ws && this.ws.reconnectNow) {
        this.ws.reconnectNow()
      }
    }
  }
}
</script>

<style scoped>
/* ==================== dot 模式 ==================== */
.ws-dot-wrap {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 9999;
}

.ws-dot {
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  cursor: pointer;
  transition: box-shadow 0.3s, background-color 0.3s;
  box-shadow: 0 0 0 2px #fff, 0 0 6px rgba(0, 0, 0, 0.15);
}

.ws-dot--connected {
  background-color: #67c23a;
}

.ws-dot--connecting {
  background-color: #e6a23c;
  animation: ws-pulse 1.2s ease-in-out infinite;
}

.ws-dot--reconnecting {
  background-color: #e6a23c;
  animation: ws-pulse 1.2s ease-in-out infinite;
}

.ws-dot--disconnected {
  background-color: #f56c6c;
}

.ws-dot-popup {
  position: absolute;
  bottom: 22px;
  right: 0;
  white-space: nowrap;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  color: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 8px;
}

.ws-popup--connected { background-color: #67c23a; }
.ws-popup--connecting { background-color: #e6a23c; }
.ws-popup--reconnecting { background-color: #e6a23c; }
.ws-popup--disconnected { background-color: #f56c6c; }

.ws-popup-text {
  flex-shrink: 0;
}

.ws-popup-retry {
  padding: 2px 8px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 3px;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
}

.ws-popup-retry:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* ==================== bar 模式 ==================== */
.ws-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  font-size: 13px;
  color: #fff;
  transition: background-color 0.3s ease;
}

.ws-bar--connected { background-color: #67c23a; }
.ws-bar--connecting { background-color: #e6a23c; }
.ws-bar--reconnecting { background-color: #e6a23c; }
.ws-bar--disconnected { background-color: #f56c6c; }

.ws-bar-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #fff;
  margin-right: 8px;
  flex-shrink: 0;
}

.ws-bar--reconnecting .ws-bar-dot {
  animation: ws-pulse 1.2s ease-in-out infinite;
}

.ws-bar-text {
  flex-shrink: 0;
}

.ws-bar-retry {
  margin-left: 12px;
  padding: 3px 12px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.ws-bar-retry:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* ==================== 动画 ==================== */
@keyframes ws-pulse {
  0% { box-shadow: 0 0 0 2px #fff, 0 0 6px rgba(0, 0, 0, 0.15); }
  50% { box-shadow: 0 0 0 2px #fff, 0 0 12px rgba(230, 162, 60, 0.6); }
  100% { box-shadow: 0 0 0 2px #fff, 0 0 6px rgba(0, 0, 0, 0.15); }
}

.ws-popup-enter-active,
.ws-popup-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.ws-popup-enter,
.ws-popup-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

.ws-slide-enter-active,
.ws-slide-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.ws-slide-enter,
.ws-slide-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
